import { useEffect } from "react";
import { useParams } from "react-router";
import { styled } from "styled-components";

import { bookmarksApiSlice } from "api/bookmarksApi.slice";
import { knowledgeApiSlice } from "api/knowledgeApi.slice";
import {
  ExpandableText,
  JoinUsUpgradeBanner,
  MasterDetailContentWrapper,
  SectionTitleUnderlined,
  UIActionButton,
  UIButtonsRow,
} from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import type { TopicBackLabelData } from "context/session/sessionTypes";
import { fetchTopicsForPinnedKnowledge } from "domains/pinAddReject/actions";
import { KnowledgeDetailPinButton } from "domains/pinAddReject/components/KnowledgeDetailPinButton";
import { KnowledgeRejectButton } from "domains/pinAddReject/components/KnowledgeRejectButton";
import { ArticleDetail } from "domains/publication/components/ArticleDetail";
import { useTranslationLink } from "domains/publication/useTranslationLink";
import { shareLinkAction } from "domains/share/actions";
import { KnowledgeSignalSection } from "domains/signals/components/KnowledgeSignalSection";
import { TopicSlider } from "domains/topic/components/TopicSlider";
import { KnowledgeType } from "entities/knowledge/types";
import { getCanAccessMonitor, getCanDisplayBookmarks } from "entities/permissions/selectors";

const StyledTitle = styled.h2`
  font-size: 2rem;
  margin: 0.6rem 0;
`;

const StyledContent = styled.div`
  height: 100%;
  margin: 1rem 0;
  color: ${({ theme }) => theme.colors.fillPrimary};
`;

const StyledSpaceBetweenRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledButtons = styled(UIButtonsRow)`
  margin-top: 2rem;
`;

const StyledDescription = styled(ExpandableText)`
  p {
    font-size: 1.1rem;
    line-height: 1.75rem;
    white-space: pre-line;
  }
`;

const StyledContentSeparationSpace = styled.div`
  height: 2rem;
`;

type PublicationDetailSectionProps = {
  id?: string;
  shareLink?: string;
  topicBack: TopicBackLabelData;
};

// TODO: Replace KnowledgeDetail with PublicationDetail when all consumer components use knowledge from api/api.types
export const PublicationDetailSection = ({ id, shareLink, topicBack }: PublicationDetailSectionProps) => {
  const dispatch = useDispatch();
  const { topicId } = useParams();
  const hasAccessToMonitor = useSelector(getCanAccessMonitor);
  const hasAccessToBookmarks = useSelector(getCanDisplayBookmarks);
  const track = useTrack();
  const translate = useTranslate();

  const { data: publication } = knowledgeApiSlice.endpoints.getKnowledgeById.useQuery({ id: id ?? "" });
  const [addKnowledgeToBookmarks] = bookmarksApiSlice.endpoints.addKnowledgeToBookmarks.useMutation();
  const [removeKnowledgeFromBookmarks] = bookmarksApiSlice.endpoints.removeKnowledgeFromBookmarks.useMutation();

  const { translationUrl, displayTranslationLink, translationLinkLabel, onTranslationLinkClick } = useTranslationLink({
    id: publication?.id,
    title: publication?.title,
    language: publication?.language,
    link: publication?.link,
  });

  useEffect(() => {
    if (!publication?.id) return;

    dispatch(fetchTopicsForPinnedKnowledge({ knowledgeId: publication.id }));
  }, [dispatch, publication?.id]);

  if (!publication) return null;

  const { title, summary, link, is_bookmarked, topics = [], signals } = publication;

  const onBookmarkHandle = async () => {
    if (publication.is_bookmarked) {
      await removeKnowledgeFromBookmarks({ id: publication.id });
    } else {
      await addKnowledgeToBookmarks({ id: publication.id });
    }
  };

  const trackOpenLink = () => track({ entityId: id, entityName: title, entityType: "Knowledge", name: "Open Link" });

  const onShareClick = () =>
    dispatch(
      shareLinkAction({
        id: id ?? "",
        name: title,
        link: shareLink ?? "",
        type: "Knowledge",
      }),
    );

  const renderTopicSlider = () => {
    if (!hasAccessToMonitor)
      return (
        <MasterDetailContentWrapper>
          <JoinUsUpgradeBanner trackPage="Knowledge detail" />
        </MasterDetailContentWrapper>
      );

    if (topics.length === 0) return null;

    return (
      <MasterDetailContentWrapper>
        <SectionTitleUnderlined>{translate("app.topic.related_topics")}</SectionTitleUnderlined>
        <TopicSlider
          hideFollowAction
          numberOfItems={{ small: 1, medium: 3, large: 2, extraLarge: 3, enormous: 4 }}
          topicBack={topicBack}
          topics={topics}
        />
      </MasterDetailContentWrapper>
    );
  };

  return (
    <>
      <MasterDetailContentWrapper>
        <ArticleDetail
          title={publication.title}
          imageUrl={publication.images?.medium}
          source={publication.source}
          timestamp={publication.time}
        />
        <StyledContent>
          <StyledTitle>{title}</StyledTitle>
          <StyledDescription text={summary} />
          <StyledSpaceBetweenRow>
            <StyledButtons>
              <UIActionButton type="open" href={link} onClick={trackOpenLink} />
              {shareLink && id && <UIActionButton type="share" onClick={onShareClick} />}
              {hasAccessToBookmarks && (
                <UIActionButton type="bookmark" isActive={is_bookmarked} onClick={onBookmarkHandle} />
              )}
              {displayTranslationLink && (
                <UIActionButton
                  type="open"
                  href={translationUrl}
                  onClick={onTranslationLinkClick}
                  label={translationLinkLabel}
                />
              )}
            </StyledButtons>
            <StyledButtons>
              {topicId && (
                <KnowledgeRejectButton
                  knowledgeId={publication.id}
                  knowledgeType={KnowledgeType.article}
                  onSuccessPath={`/topics/${topicId}/publications`}
                  topicId={topicId}
                />
              )}
              {topicId && (
                <KnowledgeDetailPinButton
                  topicId={topicId}
                  knowledgeId={publication.id}
                  isPinned={!!publication.is_pinned}
                />
              )}
            </StyledButtons>
          </StyledSpaceBetweenRow>
        </StyledContent>
        <KnowledgeSignalSection signals={signals} />
      </MasterDetailContentWrapper>
      <StyledContentSeparationSpace />
      {renderTopicSlider()}
    </>
  );
};
